import {
  includesEnumValue,
  OrdStatusEnum,
  QuoteStatusEnum,
  toBigWithDefault,
  type DistributionPart,
} from '@talos/kyoko';
import Big from 'big.js';
import { isOrderRow, isQuoteRow, type OrderRow, type QuoteRow } from './types';

const pendingFixingQuoteStatuses: QuoteStatusEnum[] = [QuoteStatusEnum.PendingFix];
const doneQuoteStatuses: QuoteStatusEnum[] = [QuoteStatusEnum.Filled];
export const workingQuoteStatuses: QuoteStatusEnum[] = [
  QuoteStatusEnum.Open,
  QuoteStatusEnum.PendingFill,
  QuoteStatusEnum.PendingNew,
];

export const doneOrdStatuses: OrdStatusEnum[] = [OrdStatusEnum.Filled, OrdStatusEnum.DoneForDay];
export const workingOrdStatuses: OrdStatusEnum[] = [
  OrdStatusEnum.New,
  OrdStatusEnum.PartiallyFilled,
  OrdStatusEnum.PendingNew,
  OrdStatusEnum.PendingCancel,
  OrdStatusEnum.PendingReplace,
  OrdStatusEnum.Replaced,
];

export const PROGRESS_COLORS = {
  done: 'var(--colors-green-lighten)',
  pendingFixing: 'var(--colors-blue-lighten)',
  working: 'var(--colors-blue-lighten)',
} as const;

export interface CareOrderProgressBar {
  id: 'done' | 'pendingFixing' | 'working';
  valuePercent: Big;
  valueNotional: Big;
  color: DistributionPart['color'];
  appearance?: DistributionPart['appearance'];
}

export function getCareOrderCountLabel(count: number) {
  return `${count} Care Order${count === 1 ? '' : 's'}`;
}

/**
 * Given all our child rows, iterate over them and throw the qtys into status buckets done, pendingFixing, working
 */
export function getCareOrderNotionalByStatus(childRows: { data: OrderRow | QuoteRow }[]) {
  const done = { value: Big(0) };
  const pendingFixing = { value: Big(0) };
  const working = { value: Big(0) };

  // Compute notional values
  for (const row of childRows) {
    let value: string | undefined;
    // Used as a mutable reference
    let target: { value: Big } | undefined = undefined;

    if (isQuoteRow(row.data)) {
      const quote = row.data;
      value = quote.OrderQty;
      if (includesEnumValue(pendingFixingQuoteStatuses, quote.QuoteStatus)) {
        target = pendingFixing;
      } else if (includesEnumValue(doneQuoteStatuses, quote.QuoteStatus)) {
        target = done;
      } else if (includesEnumValue(workingQuoteStatuses, quote.QuoteStatus)) {
        target = working;
      }
    } else if (isOrderRow(row.data)) {
      const order = row.data;
      value = order.OrderQty;
      if (includesEnumValue(doneOrdStatuses, order.OrdStatus)) {
        target = done;
      } else if (includesEnumValue(workingOrdStatuses, order.OrdStatus)) {
        target = working;
      }
    }

    if (value == null || target == null) {
      continue;
    }

    target.value = target.value.plus(toBigWithDefault(value, 0));
  }

  return { done: done.value, pendingFixing: pendingFixing.value, working: working.value };
}
